import { DEFAULT_EQUIPMENT_PURPOSE } from '@/types/equipment'
import type { Equipment, EquipmentLocation, EventType } from '@/types/equipment'

/**
 * Seed record — registry details (model/serial/vendor/installed) are derived
 * below, and `purpose` is optional because these 31 fixtures are all processing
 * equipment. Restating PROCESSING on every one of them would add noise without
 * adding information; a storage fixture states it explicitly and stands out.
 *
 * `maintained` is optional for the same reason: all 31 are serviced on a cycle,
 * so only a passive fixture would say otherwise.
 */
type EquipmentSeed = Omit<
  Equipment,
  'model' | 'serial' | 'vendor' | 'installed' | 'purpose' | 'maintained'
> & {
  purpose?: Equipment['purpose']
  maintained?: Equipment['maintained']
}

// ─── Mock equipment fleet ───────────────────────────────────────────────────
//
// 31 equipment units across 29 equipment types, spanning all 5 groups. Bay
// numbers / suite letters mirror the Boston GMF Facility floor plan used in
// the planner deck. Schedules cover every cycle column and both routine
// (chip) and non-routine (Calendar) events so the grid has real variety.

function building(floor: string, bay: string): EquipmentLocation {
  return {
    site: 'Building-5',
    floor,
    bay,
    display: `Building-5 / Floor-${floor} / Bay-${bay}`,
  }
}

function suite(name: string): EquipmentLocation {
  return { site: `Suite-${name}`, display: `Suite-${name}` }
}

const ET = {
  pm: 'preventive_maintenance' as EventType,
  cm: 'corrective_maintenance' as EventType,
  clean: 'cleaning' as EventType,
  cal: 'calibration' as EventType,
  insp: 'inspection' as EventType,
  verify: 'verification' as EventType,
  pq: 'performance_qualification' as EventType,
}

const equipmentSeed: EquipmentSeed[] = [
  // ─── Cell Collection (Apheresis) — Building-5 / Floor-1 ────────────────────
  {
    id: 'eq-aph-a1',
    equipmentId: 'APH-A1',
    equipmentType: 'Apheresis machines',
    group: 'Cell Collection (Apheresis)',
    location: building('1', '4'),
    description: 'Continuous-flow apheresis machine used for mononuclear cell collection.',
    schedule: {
      daily: { eventTypes: [ET.clean, ET.insp] },
      weekly: { eventTypes: [ET.pm], note: '2×/wk + M' },
      monthly: { eventTypes: [ET.cal] },
      quarterly: { eventTypes: [ET.pm] },
    },
    calendar: {
      events: [
        { date: '2026-09-15', event: 'Annual Calibration' },
        { date: '2026-11-02', event: 'Preventive Maintenance (Vendor)' },
      ],
    },
  },
  {
    id: 'eq-aph-a2',
    equipmentId: 'APH-A2',
    equipmentType: 'Apheresis machines',
    group: 'Cell Collection (Apheresis)',
    location: building('1', '5'),
    description: 'Continuous-flow apheresis machine used for mononuclear cell collection.',
    schedule: {
      daily: { eventTypes: [ET.clean, ET.insp] },
      weekly: { eventTypes: [ET.pm], note: '2×/wk + M' },
      monthly: { eventTypes: [ET.cal] },
      quarterly: { eventTypes: [ET.pm] },
    },
    calendar: {
      events: [
        { date: '2026-09-15', event: 'Annual Calibration' },
        { date: '2026-11-02', event: 'Preventive Maintenance (Vendor)' },
      ],
    },
  },
  {
    id: 'eq-aph-a3',
    equipmentId: 'APH-A3',
    equipmentType: 'Apheresis machines',
    group: 'Cell Collection (Apheresis)',
    location: building('1', '6'),
    description: 'Continuous-flow apheresis machine used for mononuclear cell collection.',
    schedule: {
      daily: { eventTypes: [ET.clean, ET.insp] },
      weekly: { eventTypes: [ET.pm], note: '2×/wk + M' },
      monthly: { eventTypes: [ET.cal] },
      quarterly: { eventTypes: [ET.pm] },
    },
    calendar: {
      events: [
        { date: '2026-09-15', event: 'Annual Calibration' },
        { date: '2026-11-02', event: 'Preventive Maintenance (Vendor)' },
      ],
    },
  },
  {
    id: 'eq-psep-a1',
    equipmentId: 'PSEP-A1',
    equipmentType: 'Plasma separators',
    group: 'Cell Collection (Apheresis)',
    location: building('1', '2'),
    description: 'Centrifugal plasma separator supporting the collection suite.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      monthly: { eventTypes: [ET.cal] },
      semiannually: { eventTypes: [ET.pm] },
    },
  },
  {
    id: 'eq-cscl-a1',
    equipmentId: 'CSCL-A1',
    equipmentType: 'Collection scales',
    group: 'Cell Collection (Apheresis)',
    location: building('1', '2'),
    description: 'Calibrated scale for tracking collected product weight/volume.',
    schedule: {
      monthly: { eventTypes: [ET.cal] },
      yearly: { eventTypes: [ET.verify] },
    },
  },
  {
    id: 'eq-tsl-a1',
    equipmentId: 'TSL-A1',
    equipmentType: 'Tube sealers',
    group: 'Cell Collection (Apheresis)',
    location: building('1', '3'),
    description: 'Heat sealer for closing collection tubing sets.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      quarterly: { eventTypes: [ET.pm] },
    },
  },
  {
    id: 'eq-bcs-a1',
    equipmentId: 'BCS-A1',
    equipmentType: 'Barcode printers/scanners',
    group: 'Cell Collection (Apheresis)',
    location: building('1', '1'),
    description: 'Chain-of-identity label printer/scanner at intake.',
    schedule: {
      monthly: { eventTypes: [ET.insp] },
      yearly: { eventTypes: [ET.cal] },
    },
  },

  // ─── Cell Processing — Suite-A ──────────────────────────────────────────────
  {
    id: 'eq-bsc2-a1',
    equipmentId: 'BSC2-A1',
    equipmentType: 'Biological safety cabinets (Class II)',
    group: 'Cell Processing',
    location: suite('A'),
    description: 'Class II biosafety cabinet for aseptic open-system processing.',
    schedule: {
      weekly: { eventTypes: [ET.clean], note: '3×/wk + M' },
      yearly: { eventTypes: [ET.verify] },
    },
    calendar: {
      events: [{ date: '2026-10-05', event: 'Requalification (IQ/OQ/PQ)' }],
    },
  },
  {
    id: 'eq-lfh-a1',
    equipmentId: 'LFH-A1',
    equipmentType: 'Laminar flow hoods',
    group: 'Cell Processing',
    location: suite('A'),
    description: 'Laminar flow hood for sterile bench-top manipulations.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      yearly: { eventTypes: [ET.verify] },
    },
  },
  {
    id: 'eq-cent-a1',
    equipmentId: 'CENT-A1',
    equipmentType: 'Centrifuges',
    group: 'Cell Processing',
    location: suite('A'),
    description: 'Bench-top centrifuge used for wash/concentration steps.',
    schedule: {
      daily: { eventTypes: [ET.clean] },
      semiannually: { eventTypes: [ET.cal] },
    },
  },

  // ─── Cell Expansion & Culture — Suite-B / C / D ───────────────────────────
  {
    id: 'eq-stsl-a1',
    equipmentId: 'STSL-A1',
    equipmentType: 'Sterile tube sealers',
    group: 'Cell Expansion & Culture',
    location: suite('B'),
    description: 'Sterile welding/sealing device for closed-system bag transfers.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      quarterly: { eventTypes: [ET.pm] },
    },
  },
  {
    id: 'eq-mix-a1',
    equipmentId: 'MIX-A1',
    equipmentType: 'Mixing devices',
    group: 'Cell Expansion & Culture',
    location: suite('B'),
    description: 'Bag/media mixer used during culture feeds.',
    schedule: {
      monthly: { eventTypes: [ET.pm] },
      yearly: { eventTypes: [ET.cal] },
    },
  },
  {
    id: 'eq-oshk-a1',
    equipmentId: 'OSHK-A1',
    equipmentType: 'Orbital shakers',
    group: 'Cell Expansion & Culture',
    location: suite('B'),
    description: 'Orbital shaker for suspension culture agitation.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      quarterly: { eventTypes: [ET.cal] },
    },
  },
  {
    id: 'eq-rplt-a1',
    equipmentId: 'RPLT-A1',
    equipmentType: 'Rocking platforms',
    group: 'Cell Expansion & Culture',
    location: suite('B'),
    description: 'Rocking platform bioreactor support for expansion bags.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      quarterly: { eventTypes: [ET.cal] },
    },
  },
  {
    id: 'eq-co2-a1',
    equipmentId: 'CO2-A1',
    equipmentType: 'CO2 incubators',
    group: 'Cell Expansion & Culture',
    location: suite('C'),
    description: 'CO2 incubator maintaining culture temperature/gas conditions.',
    schedule: {
      daily: { eventTypes: [ET.clean, ET.insp] },
      monthly: { eventTypes: [ET.cal] },
    },
  },
  {
    id: 'eq-tgi-a1',
    equipmentId: 'TGI-A1',
    equipmentType: 'Tri-gas incubators',
    group: 'Cell Expansion & Culture',
    location: suite('C'),
    description: 'Tri-gas incubator for hypoxic culture conditions.',
    schedule: {
      daily: { eventTypes: [ET.insp] },
      monthly: { eventTypes: [ET.cal] },
    },
  },
  {
    id: 'eq-spin-a1',
    equipmentId: 'SPIN-A1',
    equipmentType: 'Spinner flasks',
    group: 'Cell Expansion & Culture',
    location: suite('C'),
    description: 'Stirred spinner flask for suspension-culture expansion.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      quarterly: { eventTypes: [ET.pm] },
    },
  },
  {
    id: 'eq-cca-a1',
    equipmentId: 'CCA-A1',
    equipmentType: 'Cell culture analyzers',
    group: 'Cell Expansion & Culture',
    location: suite('D'),
    description: 'Automated analyzer for viability/density/metabolite tracking.',
    schedule: {
      monthly: { eventTypes: [ET.cal] },
      yearly: { eventTypes: [ET.verify] },
    },
  },

  // ─── Cryopreservation — Building-5 / Floor-3 ───────────────────────────────
  {
    id: 'eq-crf-a1',
    equipmentId: 'CRF-A1',
    equipmentType: 'Controlled-rate freezers',
    group: 'Cryopreservation',
    location: building('3', '1'),
    description: 'Controlled-rate freezer for programmed cryopreservation cooling curves.',
    schedule: {
      weekly: { eventTypes: [ET.insp] },
      quarterly: { eventTypes: [ET.cal] },
      yearly: { eventTypes: [ET.pq] },
    },
  },
  {
    id: 'eq-ln2-a1',
    equipmentId: 'LN2-A1',
    equipmentType: 'LN2 storage tanks (dewars)',
    group: 'Cryopreservation',
    location: building('3', '2'),
    description: 'Vapor-phase liquid-nitrogen storage dewar for long-term product storage.',
    schedule: {
      daily: { eventTypes: [ET.insp] },
      monthly: { eventTypes: [ET.pm] },
    },
  },
  {
    id: 'eq-cf80-a1',
    equipmentId: 'CF80-A1',
    equipmentType: 'Cryo freezers (-80°C)',
    group: 'Cryopreservation',
    location: building('3', '3'),
    description: 'Mechanical -80°C freezer for interim cryogenic storage.',
    schedule: {
      daily: { eventTypes: [ET.insp] },
      monthly: { eventTypes: [ET.cal] },
    },
    calendar: {
      events: [{ date: '2026-10-05', event: 'Requalification (IQ/OQ/PQ)' }],
    },
  },
  {
    id: 'eq-cf150-a1',
    equipmentId: 'CF150-A1',
    equipmentType: 'Cryo freezers (-150°C, vapor phase)',
    group: 'Cryopreservation',
    location: building('3', '4'),
    description: 'Ultra-low vapor-phase freezer for long-term vector/product banking.',
    schedule: {
      daily: { eventTypes: [ET.insp] },
      quarterly: { eventTypes: [ET.cal] },
    },
  },
  {
    id: 'eq-dms-a1',
    equipmentId: 'DMS-A1',
    equipmentType: 'Dewar monitoring systems',
    group: 'Cryopreservation',
    location: building('3', '5'),
    description: 'Continuous LN2 level/temperature monitoring system with alarms.',
    schedule: {
      monthly: { eventTypes: [ET.verify] },
      yearly: { eventTypes: [ET.cal] },
    },
  },
  {
    id: 'eq-cwd-a1',
    equipmentId: 'CWD-A1',
    equipmentType: 'Cell washing/dilution systems',
    group: 'Cryopreservation',
    location: building('3', '6'),
    description: 'Automated closed-system washer used during post-thaw processing.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      quarterly: { eventTypes: [ET.pm] },
    },
  },
  {
    id: 'eq-sis-a1',
    equipmentId: 'SIS-A1',
    equipmentType: 'Sample inventory scanners',
    group: 'Cryopreservation',
    location: building('3', '6'),
    description: '2D barcode scanner for cryo-vial inventory reconciliation.',
    schedule: {
      monthly: { eventTypes: [ET.insp] },
      yearly: { eventTypes: [ET.cal] },
    },
  },

  // ─── Genetic Modification — Building-5 / Floor-4 ──────────────────────────
  {
    id: 'eq-ep-a1',
    equipmentId: 'EP-A1',
    equipmentType: 'Electroporators',
    group: 'Genetic Modification',
    location: building('4', '1'),
    description: 'Electroporation system for non-viral gene/RNA delivery.',
    schedule: {
      monthly: { eventTypes: [ET.cal] },
      yearly: { eventTypes: [ET.pq] },
    },
  },
  {
    id: 'eq-vvph-a1',
    equipmentId: 'VVPH-A1',
    equipmentType: 'Viral vector prep hoods',
    group: 'Genetic Modification',
    location: building('4', '2'),
    description: 'Dedicated biosafety cabinet for viral vector handling.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      yearly: { eventTypes: [ET.verify] },
    },
    calendar: {
      events: [{ date: '2026-10-05', event: 'Requalification (IQ/OQ/PQ)' }],
    },
  },
  {
    id: 'eq-nuc-a1',
    equipmentId: 'NUC-A1',
    equipmentType: 'Nucleofector systems',
    group: 'Genetic Modification',
    location: building('4', '3'),
    description: 'Nucleofection platform for high-efficiency cell engineering.',
    schedule: {
      monthly: { eventTypes: [ET.cal] },
      yearly: { eventTypes: [ET.pq] },
    },
  },
  {
    id: 'eq-mcs-a1',
    equipmentId: 'MCS-A1',
    equipmentType: 'Magnetic cell separators',
    group: 'Genetic Modification',
    location: building('4', '4'),
    description: 'Magnetic bead-based immunoselection/separation system.',
    schedule: {
      weekly: { eventTypes: [ET.clean] },
      quarterly: { eventTypes: [ET.pm] },
    },
  },
  {
    id: 'eq-fcs-a1',
    equipmentId: 'FCS-A1',
    equipmentType: 'Cell sorters (flow-based)',
    group: 'Genetic Modification',
    location: building('4', '5'),
    description: 'Flow-based cell sorter for engineered-population enrichment.',
    schedule: {
      monthly: { eventTypes: [ET.cal] },
      yearly: { eventTypes: [ET.pq] },
    },
  },
  {
    id: 'eq-tb-a1',
    equipmentId: 'TB-A1',
    equipmentType: 'Transduction bioreactors',
    group: 'Genetic Modification',
    location: building('4', '6'),
    description: 'Closed bioreactor system for viral transduction steps.',
    schedule: {
      daily: { eventTypes: [ET.clean] },
      monthly: { eventTypes: [ET.pm] },
    },
  },
]

// ─── Registry-detail enrichment ─────────────────────────────────────────────
//
// Model / Serial / Vendor / Installed (shown in the Information modal) are
// derived deterministically from the seed so the mock stays DRY and stable.

const VENDOR_BY_TYPE: Record<string, string> = {
  'Apheresis machines': 'Terumo BCT',
  'Plasma separators': 'Fresenius Kabi',
  'Collection scales': 'Sartorius',
  'Tube sealers': 'Sebra',
  'Barcode printers/scanners': 'Zebra Technologies',
  'Biological safety cabinets (Class II)': 'Thermo Fisher',
  'Laminar flow hoods': 'Thermo Fisher',
  'Centrifuges': 'Beckman Coulter',
  'Sterile tube sealers': 'Terumo BCT',
  'Mixing devices': 'Sartorius',
  'Orbital shakers': 'Eppendorf',
  'Rocking platforms': 'Cytiva',
  'CO2 incubators': 'Panasonic Healthcare',
  'Tri-gas incubators': 'Thermo Fisher',
  'Spinner flasks': 'Corning',
  'Cell culture analyzers': 'Nova Biomedical',
  'Controlled-rate freezers': 'Cytiva',
  'LN2 storage tanks (dewars)': 'Chart MVE',
  'Cryo freezers (-80°C)': 'Thermo Fisher',
  'Cryo freezers (-150°C, vapor phase)': 'Chart MVE',
  'Dewar monitoring systems': 'Chart MVE',
  'Cell washing/dilution systems': 'Cytiva',
  'Sample inventory scanners': 'Brooks Life Sciences',
  'Electroporators': 'MaxCyte',
  'Viral vector prep hoods': 'Thermo Fisher',
  'Nucleofector systems': 'Lonza',
  'Magnetic cell separators': 'Miltenyi Biotec',
  'Cell sorters (flow-based)': 'BD Biosciences',
  'Transduction bioreactors': 'Cytiva',
}

/** Deterministic 6-digit serial suffix from the equipment id. */
function serialFor(equipmentId: string): string {
  const hash = [...equipmentId].reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
  return `SN-${100000 + (hash * 7919) % 900000}`
}

export const mockEquipment: Equipment[] = equipmentSeed.map((seed, i) => ({
  ...seed,
  purpose: seed.purpose ?? DEFAULT_EQUIPMENT_PURPOSE,
  maintained: seed.maintained ?? true,
  model: `Model ${seed.equipmentId}`,
  serial: serialFor(seed.equipmentId),
  vendor: VENDOR_BY_TYPE[seed.equipmentType] ?? 'GMP Instruments',
  installed: 2019 + (i % 6),
}))
